import { Component, computed, DestroyRef, effect, inject, signal, untracked, ViewChild } from "@angular/core";
import { JobStateService } from "../job-state.store";
import { CachedProjectService } from "../../../../shared/services/cached-project.service";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, EMPTY, forkJoin, map, of, switchMap, tap } from "rxjs";
import { HorizonWithData } from "../../../../models/generalizedHorizon";
import { ImageLoaderService } from "../../../../shared/services/image-loader.service";
import { SliceType } from "../../../../models/sliceType";
import { ViewerSettingsService } from "../viewer-settings.store";
import { BatchScreenshotService } from "../batch-screenshot.service";
import { MyViewerComponent } from "../my-viewer/my-viewer.component";

@Component({
  selector: "app-crossline-slice",
  templateUrl: "./crossline-slice.component.html",
  styleUrl: "./crossline-slice.component.less",
  standalone: false,
})
export class CrosslineSliceComponent {
  @ViewChild('sliceViewer') viewer!: MyViewerComponent;

  protected viewerSettings = inject(ViewerSettingsService)
  protected jobState = inject(JobStateService);
  
  private cachedProjectService = inject(CachedProjectService);
  private cachedImageService = inject(ImageLoaderService);
  
  protected readonly displayData = signal<any>(null);
  
  protected limits = signal<{min: number, max:number}>({min: null, max: null})
  
  protected crosslineViewerHeight = computed(() => this.viewerSettings.verticalProfileMode() === 'besideInline' ? this.viewerSettings.depthViewerHeight() : this.viewerSettings.viewerHeight())

  xCrop = computed(() => [this.jobState.rangesStart.IL(), this.jobState.rangesEnd.IL()])
  yCrop = computed(() => [this.jobState.rangesStart.depth(), this.jobState.rangesEnd.depth()])
  sliceNumber = this.jobState.jobData().x2_is_inline ? SliceType.crossLine : SliceType.inline
  
  private effectiveVpRef = computed(() => {
    // Only return the ref if the model type matches
    const model = this.jobState.selectedModel();
    return model === 'Total_Vp_Update' ? this.jobState.vpRef() : null;
  });

  private effectiveAGCWindow = computed(() => {
    const model = this.jobState.selectedModel()
    return (model === "DenChange AGC" || model === "Density plus DenChange Reflectivity AGC") ? this.jobState.AGCWindow() : null
  })

  // 3. The Image + Bins Resource
  protected sliceResource = rxResource({
    // 'params' combines Global Store signals + Local Component signals
    params: () => ({
      job: this.jobState.jobData(),
      crossline: this.jobState.crossline(), // from Store
      iteration: this.jobState.iteration(), // from Store
      modelType: this.jobState.selectedModel(), // from Store
      cmap: this.jobState.selectedCmap(), // from Store
      min: this.viewerSettings.inlineMinMax().min, // for now, the crossline min max is the same as the inlinMinMax
      max: this.viewerSettings.inlineMinMax().max, // for now, the crossline min max is the same as the inlinMinMax
      vpRef: this.effectiveVpRef(),
      window: this.effectiveAGCWindow(),
      shot: this.jobState.selectedShot()
    }),

    stream: ({ params }) => {
      // Logic gate: only fetch if we have a valid Job ID and Slice number
      if (!params.job) return EMPTY;

      // Fetch both Image (Blob) and Bins (JSON) in parallel
      return this.cachedProjectService
        .getSlice(
          params.job.id,
          params.modelType,
          params.iteration,
          this.sliceNumber, // inline = 1, crossline = 2, depth = 3 [computed to resolve the assumption of x2IsInline]. also, this is untracked because this is the 'sliceType' really. so within the inline component this won't really ever change
          params.crossline,
          params.job.modelGrid.is2d,
          params.cmap,
          params.min, // minVal,
          params.max, //maxVal,
          params.shot, // this.shot,
          1,
          params.vpRef, // this.isVpReference ? this.vpIteration : 0, VP Ref for comparison models
          params.window,
          null, // this.timeSlice
        )
        .pipe(
          switchMap((res) => {
            const { sliceUrl, url, colorBarUrl, trueMax, trueMin, min, max } = res;
            
            untracked(() => {
              this.viewerSettings.setTrueLimits('crossline', trueMax, trueMin)
              this.limits.set({min, max})
            })

            return forkJoin({
              sliceImage: this.cachedImageService.getImageBase64(url),
              colorbar: this.cachedImageService.getImageBase64(colorBarUrl),
              sliceArray: this.cachedProjectService.getSliceArray(
                sliceUrl,
                this.jobState.jobData().modelGrid.nx1,
                this.jobState.jobData().modelGrid.nx3,
                trueMin,
                trueMax,
              ),
              trueMax: of(trueMax),
              trueMin: of(trueMin),
            });
          }),
          catchError((err) => {
            throw Error(
              err.error?.detail ||
                err.message ||
                "An unexpected error occurred",
            );
          }),
        );
    },
  });

  // Stable computed that ignores color changes — only re-evaluates when horizon IDs/types or slice params change
  private horizonsForFetch = computed(
    () => ({
      keys: this.jobState.selectedHorizon().map((h) => ({ id: h.id, type: h.type })),
      sliceNumber: this.jobState.jobData().x2_is_inline
        ? this.jobState.inline()
        : this.jobState.crossline(),
      sliceType: this.jobState.jobData().x2_is_inline
        ? SliceType.crossLine
        : SliceType.inline,
    }),
    {
      equal: (a, b) =>
        a.sliceNumber === b.sliceNumber &&
        a.sliceType === b.sliceType &&
        a.keys.length === b.keys.length &&
        a.keys.every((h, i) => h.id === b.keys[i].id && h.type === b.keys[i].type),
    },
  );

  // 4. Horizon data resources. will be in conjunction with selectedHorizons
  horizonsDataResource = rxResource<HorizonWithData[], ReturnType<typeof this.horizonsForFetch>>({
    params: () => this.horizonsForFetch(),
    stream: ({ params }) => {
      if (params.keys.length == 0) return of([]);

      const { sliceNumber, sliceType, keys } = params;
      const jobId = this.jobState.jobData().id;
      const selectedHorizon = untracked(() => this.jobState.selectedHorizon());
      const requests = keys.map((key, i) => {
        const h = selectedHorizon[i];
        const fetch$ =
          key.type === "Project"
            ? this.cachedProjectService.getProjectHorizonById(key.id, sliceNumber, sliceType)
            : this.cachedProjectService.getHorizon(jobId, sliceNumber, sliceType, key.id);
        return fetch$.pipe(map((data) => ({ ...h, data })));
      });
      return forkJoin(requests);
    },
  });

  // 5. Sonic log resources. will be in conjuntion with selectedSonic
  sonicLogDataResource = rxResource({
    params: () => ({ selectedSonic: this.jobState.selectedLog(), sonicFrequency: this.jobState.logFrequency() }),
    stream: ({ params }) => {
      if (!params.selectedSonic) return of(null);
      else {
        return this.cachedProjectService
          .getSonicLog(params.selectedSonic.id, params.sonicFrequency)
          .pipe(
            map((res: any) => {
              return {
                data: res.log.map((dp) => ({ depth: dp.x, velocity: dp.y })),
                x: params.selectedSonic.inline - 1, // the -1 is used to position corectly. its also used when assigning the location. It will perhaps be relevant to figure how to actually save them because it is quite confusing right now
                markers: res.markers
              };
            }),
          );
      }
    },
  });

  // Clean up Object URLs to prevent memory leaks
  constructor() {
    // report image loading to the batch screenshot runner so it knows when the
    // viewer has settled after a job/iteration change
    const batchScreenshotService = inject(BatchScreenshotService);
    effect(() => batchScreenshotService.reportSliceLoading("crossline", this.sliceResource.isLoading()));
    inject(DestroyRef).onDestroy(() => batchScreenshotService.reportSliceLoading("crossline", false));

    effect((onCleanup) => {
      if (this.sliceResource.error()) return;
      
      const data: { url: string; bins: any } =
        this.sliceResource.value() as any;
      if (data?.url) {
        onCleanup(() => URL.revokeObjectURL(data.url));
      }
    });

    effect(() => {
      if (this.sliceResource.error()) return;

      const val = this.sliceResource.value();
      if (val) {
        this.displayData.set(val);
      }
    });
  }

  resetZoom() { 
    this.viewer.resetZoom()
  }
}
